import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { ProjectService } from 'src/app/services/project.service';
import Project from 'src/app/interfaces/Project';
import User from 'src/app/interfaces/User';
import { InstitutionService } from 'src/app/services/institution.service';
import { EventService } from 'src/app/services/event.service';
import { ForumService } from 'src/app/services/forum.service';
import Institution from 'src/app/interfaces/Institution';
import Event from 'src/app/interfaces/Event';
import Discussion from 'src/app/interfaces/Discussion';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ResourceService } from 'src/app/services/resource.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  currentUser: User;
  projects: Array<Project> = [];
  institutions: Array<Institution> = [];
  events: Array<Event> = [];
  discussionIds = [];
  discussionIds$ = new BehaviorSubject([]);
  discussions: Array<Discussion> = [];

  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    private institutionService: InstitutionService,
    private eventService: EventService,
    private forumService: ForumService,
    private analyticsService: AnalyticsService,
    private resourceService: ResourceService,
  ) {}

  ngOnInit() {
    this.analyticsService.event('dashboard_view');
    this.subscriptions.add(
      this.userService.user.subscribe((u: User) => {
        if (u && u.uid) {
          this.currentUser = u;
          this.updateUserAnalytics();
          if (this.currentUser.projects && this.currentUser.projects.length) {
            this.projectService.getCurrentUserProjects(this.currentUser.projects).subscribe((projArr) => {
              if (projArr.length) {
                projArr.forEach((p$) => {
                  p$.subscribe((p) => {
                    if (p && this.currentUser.projectRoles[p[0].id]) {
                      this.projects.push(p[0]);
                    }
                  });
                });
              }
            });
          }

          if (this.currentUser.institutions && this.currentUser.institutions.length) {
            this.institutionService.getCurrentUserInstitutions(this.currentUser.institutions).subscribe((instArr) => {
              if (instArr.length) {
                instArr.forEach((i$) => {
                  i$.subscribe((i) => {
                    if (i) {
                      this.institutions.push(i[0]);
                    }
                  });
                });
              }
            });
          }

          this.forumService.getUserDiscussions(this.currentUser.uid).subscribe((discObs: any) => {
            if (discObs.length) {
              discObs.forEach((discArr$, i) => {
                discArr$.subscribe((disc) => {
                  if (i === 0 && disc) {
                    this.discussions = this.discussions.concat(disc);
                  } else if (i === 1 && disc) {
                    let dIds = disc.map((d) => d.discussion);
                    dIds.forEach((id) => {
                      if (this.discussionIds.indexOf(id) === -1) {
                        this.discussionIds.push(id);
                      }
                    });
                    this.discussionIds$.next(this.discussionIds);
                  }
                });
              });
            }
          });
          this.discussionIds$.subscribe((dIds) => {
            dIds.forEach((id) => {
              if (this.discussions.length) {
                if (!this.discussions.some((d) => d.id === id)) {
                  this.forumService.getDiscussion(id).then((disc: any) => {
                    if (disc) {
                      this.discussions.push(disc);
                    }
                  });
                }
              } else {
                this.forumService.getDiscussion(id).then((disc: any) => {
                  if (disc) {
                    this.discussions.push(disc);
                  }
                });
              }
            });
          });

          if (this.currentUser.events && this.currentUser.events.length) {
            this.eventService.getCurrentUserEvents(this.currentUser.events).subscribe((eventsArr) => {
              if (eventsArr.length) {
                eventsArr.forEach((e$) => {
                  e$.subscribe((e) => {
                    if (e) {
                      this.events.push(e[0]);
                    }
                  });
                });
              }
            });
          }
        }
      })
    );
  }

  dismissBanner() {
    this.currentUser.banner.dismissed = true;
    this.userService.saveUser(this.currentUser.uid, this.currentUser);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  updateUserAnalytics() {
    const userProps = {
      has_bio: !!this.currentUser.bio,
      comments: this.currentUser.comments ? this.currentUser.comments.length : 0,
      country: this.currentUser.country ? this.currentUser.country : '',
      disabled: this.currentUser.disabled,
      display_name: this.currentUser.displayName,
      email: this.currentUser.email,
      events: this.currentUser.events.length,
      expertise: this.currentUser.expertise ? this.currentUser.expertise.length : 0,
      institutions: this.currentUser.institutions.length,
      profile_picture: !!this.currentUser.profilePicture,
      projects: this.currentUser.projects.length,
      notification_tags: !!this.currentUser.notificationSettings.tags ? this.currentUser.notificationSettings.tags : []
    };
    this.analyticsService.updateUserProperties(userProps);
  }

  trackEvent(name: string): void {
    this.analyticsService.event(name);
  }


  async updateApproaches() {
    const discussions = await this.forumService.getAllDiscussions().toPromise();
    const institutions = await this.institutionService.getAllInstitutions().toPromise();
    const projects = await this.projectService.getAllProjects().toPromise();
    const resources = await this.resourceService.getAllResources().toPromise();
    const users = await this.userService.getMembers().toPromise();
    
    discussions.forEach((discussion, i )=> {
      if ('approaches' in discussion && discussion.approaches.length) {
        discussion.approaches = this.updateApproachesArray(discussion.approaches)
        this.forumService.updateDiscussion(discussion.id, discussion);
      }
    })

    institutions.forEach((institution, i )=> {
      if ('expertise' in institution && institution.expertise.length) {
        institution.expertise = this.updateApproachesArray(institution.expertise)
        this.institutionService.updateInstitution(institution.id, institution)
      }
    })

    projects.forEach((project, i )=> {
      if ('approaches' in project && project.approaches.length) {
        project.approaches = this.updateApproachesArray(project.approaches)
        this.projectService.updateProject(project.id, project)
      }
    })

    resources.forEach((resource, i )=> {
      if ('approaches' in resource && resource.approaches.length) {
        resource.approaches = this.updateApproachesArray(resource.approaches)
        this.resourceService.updateResource(resource.id, resource)
      }
    })

    users.forEach((user, i )=> {
      if ('expertise' in user && user.expertise !== null && user.expertise.length) {
        user.expertise = this.updateApproachesArray(user.expertise)
        this.userService.saveUser(user.uid, user)
      }
    })

  }

  updateApproachesArray(arr) {
    let approaches = [...arr]

    // update fertilization and upwelling
    if (approaches.includes('fertilization')) approaches.push('microalgae')
    if (approaches.includes('upwelling')) approaches.push('microalgae')

    // update other-bio and other-abio
    if (approaches.includes('other-bio')) approaches.push('other')
    if (approaches.includes('other-abio')) approaches.push('other')

    // remove duplicates
    approaches = [...new Set(approaches)]

    return approaches
  }
}
